import { useCallback, useEffect, useState } from "react";
import config from "@/config";
import type {
  WebhookFailureCountResponse,
  WebhookFailureDetailResponse,
  WebhookFailureListResponse,
  WebhookFailureRecord,
  WebhookRetryResponse,
} from "@/types/webhook-failures";

const DEFAULT_COUNT_POLL_MS = 60_000;

export function useWebhookFailureCount(pollMs = DEFAULT_COUNT_POLL_MS) {
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/api/dashboard/webhook-failures/count`,
      );
      if (!res.ok) {
        throw new Error(`Failed to load webhook failure count (${res.status})`);
      }
      const data = (await res.json()) as WebhookFailureCountResponse;
      setTotal(typeof data.total === "number" ? data.total : 0);
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "Failed to load webhook failure count";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    if (!pollMs || pollMs <= 0) return;
    const id = window.setInterval(() => void refresh(), pollMs);
    return () => window.clearInterval(id);
  }, [refresh, pollMs]);

  return { total, loading, error, refresh };
}

export function useWebhookFailures(limit = 100) {
  const [failures, setFailures] = useState<WebhookFailureRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/api/dashboard/webhook-failures?limit=${limit}`,
      );
      if (!res.ok) {
        throw new Error(`Failed to load webhook failures (${res.status})`);
      }
      const data = (await res.json()) as WebhookFailureListResponse;
      setFailures(data.failures || []);
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "Failed to load webhook failures";
      setError(msg);
      setFailures([]);
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { failures, loading, error, refresh };
}

export function useWebhookFailureDetail(caseId: string | null) {
  const [detail, setDetail] = useState<WebhookFailureDetailResponse | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const path = encodeURIComponent(id);
      const res = await fetch(
        `${config.API_BASE_URL}/api/dashboard/webhook-failures/${path}`,
      );
      if (!res.ok) {
        let detailMsg = "";
        try {
          const body = (await res.json()) as { detail?: string };
          if (typeof body.detail === "string") detailMsg = body.detail;
        } catch {
          // ignore
        }
        throw new Error(detailMsg || `Failed to load detail (${res.status})`);
      }
      const data = (await res.json()) as WebhookFailureDetailResponse;
      setDetail(data);
      return data;
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to load detail";
      setError(msg);
      setDetail(null);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!caseId) {
      setDetail(null);
      setError(null);
      return;
    }
    void load(caseId);
  }, [caseId, load]);

  return { detail, loading, error, reload: load };
}

export function useWebhookRetry() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const retry = useCallback(async (caseId: string) => {
    setLoading(true);
    setError(null);
    try {
      const path = encodeURIComponent(caseId);
      const res = await fetch(
        `${config.API_BASE_URL}/api/dashboard/webhook-failures/${path}/retry`,
        { method: "POST" },
      );
      if (!res.ok) {
        let detailMsg = "";
        try {
          const body = (await res.json()) as { detail?: string };
          if (typeof body.detail === "string") detailMsg = body.detail;
        } catch {
          // ignore
        }
        throw new Error(detailMsg || `Retry failed (${res.status})`);
      }
      return (await res.json()) as WebhookRetryResponse;
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Retry failed";
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { retry, loading, error };
}
